import { Alert, Box, Container, Link, Typography, useTheme } from "@mui/material";
import { NavigationAndFooterLayout } from "../layouts";
import { NextPageWithLayout } from "@cm-websites/common";
import { NextSeo } from "next-seo";
import Image from "next/image";
import NextLink from "next/link";
import React from "react";
import TatortEsstisch from "../../public/images/tatort-esstisch.jpg";

export const SpielregelnPage: NextPageWithLayout = () => {
  const theme = useTheme();

  return (
    <React.Fragment>
      <NextSeo
        description="Unsere Krimidinner folgen einfach zu verstehenden Regeln. Da diese auf nur eine Seite passen, finden sich auch Einsteiger sofort zurecht."
        title="Spielregeln und Ablauf | Culinario Mortale®"
      />
      <Container
        maxWidth="md"
        sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}
      >
        <Typography variant="h1" sx={{ pb: 6 }}>
          Regeln eines Krimidinners
        </Typography>

        <Typography variant="body1" paragraph>
          Gemeinsam sitzt ihr nach der Tat zusammen und versucht, eure Erinnerungen zusammenzutragen, um
          dem Täter auf die Schliche zu kommen. Dabei ruft ihr euch mit jeder Spielrunde weitere
          Erinnerungen an den Tathergang ins Gedächtnis, denn jede Runde behandelt einen anderen
          Zeitabschnitt des Tatabends. Das bedeutet, dass die Tat erst in Runde 3 vollständig
          rekonstruiert werden kann. Natürlich könnt ihr in eurem Spielheft jederzeit zurückblättern. Ihr
          entscheidet als Gruppe, ab wann sich eure Diskussionen im Kreis drehen und ihr zur nächsten
          Runde übergehen wollt. Ein grober Richtwert sind 30 – 60 Minuten pro Runde.
        </Typography>

        <Typography variant="body1" paragraph>
          Eure Spielhefte sind jeweils nur für eure eigenen Augen bestimmt. Lediglich die Abschnitte über
          Spielregeln, Spielablauf und Vorgeschichte sind für alle Spieler gleich. Hinweise zu eurer
          eigenen Person dürft ihr verschweigen, aber ihr dürft nicht lügen! Werdet ihr konkret nach
          einer Sache gefragt, müsst ihr die Wahrheit sagen. Das Recht zu lügen und andere Spieler auf
          eine falsche Fährte zu locken, hat allein der Täter. Ihr könnt jedoch versuchen, die Wahrheit
          möglichst vorteilhaft für euch auszulegen. Dabei entscheidet ihr selbst, welche Informationen
          ihr teilen wollt. Doch Vorsicht! Teilt ihr zu wenig Informationen mit der Gruppe, könnt ihr den
          Fall nicht lösen. Nur gemeinsam habt ihr Erfolg! Teilt ihr zu viel und zu früh, geratet ihr
          möglicherweise selbst unter Verdacht. Solltet ihr unabsichtlich gelogen haben, obwohl ihr nicht
          der Täter seid, dann versucht, euren Fehler schnellstmöglich zu korrigieren.
        </Typography>

        <Typography variant="body1" paragraph>
          Ob ihr der Täter seid, erfahrt ihr im ersten Abschnitt eures Spielhefts mit den Informationen
          über eure Figur. Abwesende Figuren kommen nicht als Täter in Betracht. Für sie gilt die
          Unschuldsvermutung.
        </Typography>

        <Alert severity="warning">
          Die obenstehenden Regeln sowie den Abschnitt über den Spielablauf findet ihr übrigens auch in
          jedem Spielheft.
        </Alert>

        <Typography component="h2" variant="h1" sx={{ pt: 12, pb: 6 }}>
          Ablauf eines Krimidinners
        </Typography>

        <Typography variant="body1" paragraph>
          Jedes Culinario Mortale besteht aus drei Runden und einem klar strukturierten Spielablauf. Wir
          haben für euch die einzelnen Spielphasen aufgelistet.
        </Typography>
        <Typography variant="body1" paragraph>
          Wir haben das Thema „Essen“ bewusst aus dem Spielablauf herausgehalten. Je nach persönlicher
          Vorliebe und Anzahl der Gänge könnt ihr euer Menü auf verschiedene Arten in das Krimidinner
          integrieren: Entweder lasst ihr längere Pausen zwischen den einzelnen Runden, in denen dann
          gemeinsam gegessen wird, oder ihr esst während des Spiels.
        </Typography>

        <Typography component="h3" variant="h5">
          Spielbeginn
        </Typography>
        <Typography variant="body1" paragraph>
          Der Gastgeber liest die Abschnitte Die Spielregeln, Der Spielablauf (dieser Text) und Die
          Vorgeschichte vor. Alle Spieler stellen sich mit ein bis zwei Sätzen vor. Danach liest ­einer
          der Spieler aus dem Ereignisheft den Prolog vor.
        </Typography>

        <Typography component="h3" variant="h5">
          Runde 1
        </Typography>
        <Typography variant="body1" paragraph>
          Nach Aufforderung durch den Gastgeber lesen alle Spieler still Runde 1. Danach wird der erste
          Zeitabschnitt des Tatabends diskutiert. Der Gastgeber beginnt das Gespräch.
        </Typography>

        <Typography component="h3" variant="h5">
          Runde 2
        </Typography>
        <Typography variant="body1" paragraph>
          Alle Spieler lesen Runde 2. Der Gastgeber liest aus dem Ereignisheft Ereignis A vor. Danach
          wird der zweite Zeitabschnitt des Tatabends diskutiert.
        </Typography>

        <Typography component="h3" variant="h5">
          Runde 3
        </Typography>
        <Typography variant="body1" paragraph>
          Alle Spieler lesen Runde 3. Der Gastgeber liest aus dem Ereignisheft Ereignis B vor. Danach
          wird der letzte Zeitabschnitt des Tatabends diskutiert.
        </Typography>

        <Typography component="h3" variant="h5">
          Verdacht und Verteidigung
        </Typography>
        <Typography variant="body1" paragraph>
          Jeder Spieler äußert seinen Verdacht, wen er für den Täter hält. Alle Verdächtigten haben die
          Gelegenheit, sich zu verteidigen. Anschließend gibt jeder Spieler seine Stimme ab, wen er der
          Tat bezichtigt.
        </Typography>

        <Typography component="h3" variant="h5">
          Aufklärung
        </Typography>
        <Typography variant="body1" paragraph>
          Die Aufklärung wird vorgelesen. Ist es euch gelungen, den Täter zu überführen oder konnte er
          entkommen?
        </Typography>
      </Container>
    </React.Fragment>
  );
};

SpielregelnPage.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default SpielregelnPage;
